import './style.css';

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@economic/taco';
import { Button, Input, Checkbox } from '@economic/taco';
import { useCurrentAgreement, useCurrentLocale, useCurrentUser } from '@economic/react-app';

import { t, useLocalization } from '../../l10n/l10n';
import isValidEmail from '../../../util/utilities/isValidEmail';
import isValidName from '../../../util/utilities/isValidName';
import internalApiClient from '../../../util/api/internalApiClient';
import { Trans } from '@lingui/macro';
import ExternalLink from '../../ExternalLink';

enum Steps {
    Verify = 1,
    Done = 2,
}

function getLanguageIdFromLanguageCode(languageCode: string) {
    switch (languageCode) {
        case 'en-GB':
            return 2;

        case 'da-DK':
        default:
            return 1;
    }
}

function UserLinkedToVismaConnect({ headerText, contentText, onOk }) {
    return (
        <div className="vismaconnect-popups message-popup relative text-center m-1" style={{ maxWidth: '450px' }}>
            <div className="vismaconnect-popups message-popup-head rounded text-center mb-3 font-normal align-middle text-2xl">
                <span className="vismaconnect-popups popup__head__lbl">{headerText}</span>
            </div>
            <div
                className="vismaconnect-popups message-popup-content mt-1 mx-5 text-center"
                dangerouslySetInnerHTML={{ __html: contentText }}></div>

            <div>
                <Button type="button" appearance="primary" onClick={onOk}>
                    {' '}
                    {t`SHARED_CONFIRM_YES`}{' '}
                </Button>
            </div>
        </div>
    );
}

function FooterError({ error }) {
    return (
        <div className="vismaconnect-popups m-5" style={{ maxWidth: '750px' }}>
            <div className="vismaconnect-popups error-message-body py-1 px-0 text-red font-bold pl-5 inline-block">{error}</div>
        </div>
    );
}

const CheckBoxControlWithHtml = ({ value, label, onChange }) => (
    <div className="items-start cursor-pointer text-xs my-1">
        <Checkbox className="flex-1 h-auto" checked={value} onChange={checked => onChange(checked)} />
        <span className="pl-3 whitespace-normal font-bold text-purple-dark">{label}</span>
    </div>
);

interface Props {
    enforcePopup: boolean;
    displayNewsletter: boolean;
    isForInvite: boolean;
    firstName: string;
    lastName: string;
    email: string;
    onDone: () => void;
}

export default function VismaConnectVerifyInformationDialog({
    enforcePopup,
    displayNewsletter,
    isForInvite,
    firstName,
    lastName,
    email,
    onDone,
}: Props) {
    const navigate = useNavigate();
    const loadingTexts = useLocalization(textIds);
    const agreement = useCurrentAgreement();
    const locale = useCurrentLocale();
    const user = useCurrentUser();
    const [show, setShow] = useState(true);
    const [step, setStep] = useState(Steps.Verify);
    const [state, setState] = useState({
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        email: '', // to start with an empty email initially.
        error: null,
        isNewsletterEnabled: false,
        disableJoinButton: false,
        finalMessageHader: '',
        finalMessageContent: '',
        redirect: '',
    });

    if (loadingTexts || !show) {
        return null;
    }

    const getVismaConnectErrorPageUrl = () => {
        const redirectUrl = window.top?.firebird.Econ.applicationUrl('/vc/error.aspx?error=14');
        const url = '/logout?redirectUrl=' + encodeURIComponent(redirectUrl);
        return url;
    };

    const subscribeToNewsletter = async requestModel => {
        await internalApiClient.post(`/users/subscribe`, requestModel);
    };

    const linkCurrentUser = async requestModel => {
        await internalApiClient
            .post(`/users/link`, requestModel)
            .then(async response => {
                state.disableJoinButton = false;

                if (state.isNewsletterEnabled) {
                    await subscribeToNewsletter(requestModel);
                }

                if (enforcePopup) {
                    setShow(false);
                    const url = getVismaConnectErrorPageUrl();
                    navigate(url);
                } else {
                    setStep(Steps.Done);
                    setState({ ...state, finalMessageHader: response.data.header, finalMessageContent: response.data.content });
                }
            })
            .catch(error => {
                setState({ ...state, error: error.error || error.response.data, disableJoinButton: false });
            });
    };

    var titleText = isForInvite ? t`CONNECT_VERIFY_DETAILS_MODAL_TITLE_INVITE` : t`CONNECT_VERIFY_DETAILS_MODAL_TITLE`;
    var joinButtonText = isForInvite
        ? t`CONNECT_VERIFY_DETAILS_MODAL_JOIN_CONNECT_INVITE`
        : t`CONNECT_VERIFY_DETAILS_MODAL_JOIN_CONNECT`;

    if (step === Steps.Done) {
        return (
            <Dialog defaultOpen showCloseButton={false} closeOnEscape={false} size="xs">
                <Dialog.Content aria-label="">
                    <UserLinkedToVismaConnect
                        headerText={state.finalMessageHader}
                        contentText={state.finalMessageContent}
                        onOk={() => {
                            onDone();
                            setShow(false);
                        }}
                    />
                </Dialog.Content>
            </Dialog>
        );
    }

    return (
        <Dialog defaultOpen showCloseButton={!enforcePopup} closeOnEscape={!enforcePopup} size="xs">
            <Dialog.Content aria-label={titleText}>
                <div className="vismaconnect-popups connect-verify-email-main p-8 pb-0 relative h-auto">
                    <div className="vismaconnect-popups pt-0 px-5">
                        <h1 className="vismaconnect-popups text-center">{titleText}</h1>

                        <p className="vismaconnect-popups text-center">{t`CONNECT_VERIFY_DETAILS_MODAL_SUBTITLE`}</p>

                        <p>
                            {isForInvite ? (
                                <Trans>
                                    Please enter the email of the invited user. It's important that he or she have personal access
                                    to the email, this will be used to sign in to e-conomic moving forward. Also, please confirm
                                    that the users' full name is entered correctly in the fields below.
                                </Trans>
                            ) : (
                                <Trans>
                                    Please enter the email you want to use for signing in to e-conomic. It's important that you
                                    have personal access to the email you enter. Also, please confirm that your full name is
                                    entered correctly in the fields below.
                                </Trans>
                            )}
                        </p>

                        <label>
                            {t`CONNECT_VERIFY_DETAILS_MODAL_LABEL_EMAIL`}
                            <Input
                                className="vismaconnect-popups w-full font-normal"
                                type="email"
                                value={state.email}
                                onChange={e => onEmailChange(e.target.value)}
                                required
                            />
                        </label>

                        <label>
                            {t`CONNECT_VERIFY_DETAILS_MODAL_LABEL_FIRST_NAME`}
                            <Input
                                className="vismaconnect-popups w-full font-normal"
                                type="text"
                                value={state.firstName}
                                onChange={e => onFirstNameChange(e.target.value)}
                                required
                            />
                        </label>

                        <label>
                            {t`CONNECT_VERIFY_DETAILS_MODAL_LABEL_LAST_NAME`}
                            <Input
                                className="vismaconnect-popups w-full font-normal"
                                type="text"
                                value={state.lastName}
                                onChange={e => onLastNameChange(e.target.value)}
                                required
                            />
                        </label>

                        {displayNewsletter && !isForInvite && (
                            <div>
                                <p className="vismaconnect-popups mb-0">{t`CONNECT_VERIFY_CHECKBOX1`}</p>
                                <CheckBoxControlWithHtml
                                    label={
                                        <Trans>
                                            Sign me up for e-conomic newsletters, which contains tips and tricks, invitations to
                                            events and marketing communication about our accounting software and related software
                                            from our{' '}
                                            <ExternalLink href="https://www.e-conomic.dk/lpd/nyhedsmails-app-partners">
                                                app-partners
                                            </ExternalLink>
                                            . I can choose to unsubscribe from the newsletters at any time.{' '}
                                            <ExternalLink href="https://www.e-conomic.dk/persondata-nyhedsmails">
                                                Read more
                                            </ExternalLink>
                                        </Trans>
                                    }
                                    value={state.isNewsletterEnabled}
                                    onChange={(newVal: boolean) => {
                                        state.isNewsletterEnabled = newVal;
                                        setState({ ...state, isNewsletterEnabled: newVal });
                                    }}
                                />
                            </div>
                        )}

                        <Button
                            className="w-full mt-5"
                            appearance="primary"
                            type="button"
                            disabled={state.disableJoinButton || !state.firstName || !state.lastName}
                            onClick={async () => await handleSubmit(agreement, user)}>
                            {joinButtonText}
                        </Button>
                    </div>
                    {state.error ? (
                        <FooterError error={state.error === 'object' ? JSON.stringify(state.error) : state.error} />
                    ) : null}
                </div>
            </Dialog.Content>
        </Dialog>
    );

    function getValidationMessage(state) {
        if (!isValidEmail(state.email)) {
            return t`CONNECT_VERIFY_DETAILS_MODAL_NOT_VALID_EMAIL`;
        }

        if (!isValidName(state.firstName)) {
            return t`CONNECT_VERIFY_DETAILS_MODAL_NOT_VALID_FIRST_NAME`;
        }

        if (!isValidName(state.lastName)) {
            return t`CONNECT_VERIFY_DETAILS_MODAL_NOT_VALID_LAST_NAME`;
        }

        return null;
    }
    function onEmailChange(email: string) {
        setState({ ...state, email: email, error: getValidationMessage(state) });
    }

    function onFirstNameChange(name: string) {
        setState({ ...state, firstName: name, error: getValidationMessage(state) });
    }

    function onLastNameChange(lastName: string) {
        setState({ ...state, lastName: lastName, error: getValidationMessage(state) });
    }

    async function handleSubmit(agreement, user) {
        if (!isValidName(state.firstName) || !isValidName(state.lastName) || !isValidEmail(state.email)) {
            setState({ ...state, error: getValidationMessage(state) });
        }

        var requestModel = {
            id: user.guid,
            agreementId: agreement.number,
            userId: user.id,
            language: getLanguageIdFromLanguageCode(locale.languageCode),
            countryCode: locale.countryCode,
            email: state.email,
            firstName: state.firstName,
            lastName: state.lastName,
        };

        setState({ ...state, disableJoinButton: true });

        await linkCurrentUser(requestModel);
    }
}

const textIds = [
    'CONNECT_VERIFY_DETAILS_MODAL_TITLE',
    'CONNECT_VERIFY_DETAILS_MODAL_TITLE_INVITE',
    'CONNECT_VERIFY_DETAILS_MODAL_SUBTITLE',
    'CONNECT_VERIFY_DETAILS_MODAL_CONTENT',
    'CONNECT_VERIFY_DETAILS_MODAL_CONTENT_INVITE',
    'CONNECT_VERIFY_DETAILS_MODAL_JOIN_CONNECT',
    'CONNECT_VERIFY_DETAILS_MODAL_JOIN_CONNECT_INVITE',
    'CONNECT_VERIFY_DETAILS_MODAL_LABEL_EMAIL',
    'CONNECT_VERIFY_DETAILS_MODAL_LABEL_FIRST_NAME',
    'CONNECT_VERIFY_DETAILS_MODAL_LABEL_LAST_NAME',
    'CONNECT_VERIFY_DETAILS_MODAL_NOT_VALID_EMAIL',
    'CONNECT_VERIFY_DETAILS_MODAL_NOT_VALID_FIRST_NAME',
    'CONNECT_VERIFY_DETAILS_MODAL_NOT_VALID_LAST_NAME',
    'CONNECT_VERIFY_CHECKBOX1',
    'CONNECT_VERIFY_CHECKBOX2',
    'SHARED_USER_LINKED_TO_VISMA_CONNECT_HEADLINE',
    'SHARED_USER_LINKED_TO_VISMA_CONNECT_DESCRIPTION',
    'SHARED_CONFIRM_YES',
    'SHARED_USER_INVITED_TO_VISMA_CONNECT_HEADLINE',
    'SHARED_USER_INVITED_TO_VISMA_CONNECT_DESCRIPTION',
];
